/**
 * RodeoBulkDraftActions
 *
 * Header-level admin actions for a single rodeo on the Manage Draws page:
 *
 *   - Reset All Draft Draws  (wipe every DRAFT event's draft entries)
 *   - Run Initial Draw       (run the initial draw for NOT_STARTED events)
 *
 * The two actions are deliberately separate so a delegate's sandbox
 * tweaks to a DRAFT event aren't silently overwritten when the admin
 * just wants to draw newly-added (un-drawn) events. To re-draw a DRAFT
 * event, the admin clicks Reset first (downgrades it to NOT_STARTED)
 * and then clicks Run Initial Draw.
 *
 * Each button opens a confirmation modal that fetches the affected-
 * events list lazily, so this component doesn't add work to page-load.
 */

import { useEffect, useState } from 'react';
import { Play, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  BulkDraftDrawResetModal,
  type BulkDraftMode,
} from '@/components/delegate/BulkDraftDrawResetModal';
import {
  useRodeoDraftEventsPreview,
  useResetRodeoDraftDraws,
  useRunInitialRodeoDraws,
  type BulkDraftActionReport,
} from '@/api/extensions/useDrawOps';
import { useToast } from '@/contexts/ToastContext';

interface RodeoBulkDraftActionsProps {
  rodeoId: string;
  /**
   * Count of events in the rodeo currently in DRAFT placement_status.
   * Drives the disabled state on the Reset button (no point in
   * resetting nothing). The Run-Initial button is always enabled
   * because un-drawn events aren't reflected in this count — the
   * lazy preview fetch shows the real scope on click.
   */
  draftEventCount: number;
}

export function RodeoBulkDraftActions({
  rodeoId,
  draftEventCount,
}: RodeoBulkDraftActionsProps) {
  const [openMode, setOpenMode] = useState<BulkDraftMode | null>(null);
  const [result, setResult] = useState<BulkDraftActionReport | null>(null);
  const { showSuccess, showError } = useToast();

  // Lazy: only fetches when the modal is open. `staleTime:0` on the hook
  // means each open shows fresh state — important when a peer admin
  // could have published an event mid-session. The mode determines how
  // NOT_STARTED vs DRAFT events are bucketed in the preview.
  const previewQuery = useRodeoDraftEventsPreview(
    rodeoId,
    openMode !== null,
    openMode ?? 'reset',
  );

  const resetMutation = useResetRodeoDraftDraws();
  const runInitialMutation = useRunInitialRodeoDraws();
  const isRunning =
    resetMutation.isPending || runInitialMutation.isPending;

  const handleClose = () => {
    if (isRunning) return;
    setOpenMode(null);
    setResult(null);
    // Reset mutation state so a previous error doesn't bleed into the
    // next open of this modal.
    resetMutation.reset();
    runInitialMutation.reset();
  };

  // Clear stale result whenever we open a different action — keeps the
  // modal preview-first instead of jumping straight to last run's result.
  useEffect(() => {
    if (openMode === null) return;
    setResult(null);
  }, [openMode]);

  const handleConfirm = async () => {
    if (!openMode) return;
    const mutation =
      openMode === 'reset' ? resetMutation : runInitialMutation;
    try {
      const report = await mutation.mutateAsync({ rodeoId });
      setResult(report);
      if (report.failed > 0) {
        showError(
          'Some events failed',
          `${report.successful} succeeded, ${report.failed} failed.`,
        );
      } else {
        showSuccess(
          openMode === 'reset' ? 'Draft draws reset' : 'Initial draws run',
          `${report.successful} event${report.successful === 1 ? '' : 's'} ${
            openMode === 'reset' ? 'cleared' : 'drawn'
          }.`,
        );
      }
    } catch (err: any) {
      showError(
        'Bulk action failed',
        err?.message ?? 'Unexpected error — try again.',
      );
    }
  };

  const resetDisabled = draftEventCount === 0;

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpenMode('reset')}
          disabled={resetDisabled}
          title={
            resetDisabled
              ? 'No events currently in draft to reset'
              : `Reset ${draftEventCount} draft draw${
                  draftEventCount === 1 ? '' : 's'
                } (does not touch published or finalized draws)`
          }
          className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white disabled:opacity-50"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Reset All Draft Draws
        </Button>
        {/* Always enabled — preview shows whether anything is in scope
            once the admin opens it. Operates only on un-drawn events;
            DRAFT events show up as "skipped" with a clear reason. */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpenMode('run-initial')}
          title="Run initial draw for every event that hasn't been drawn yet (events with an existing draft are skipped — reset first if you want to re-run)"
          className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
        >
          <Play className="h-3.5 w-3.5 mr-1.5" />
          Run Initial Draw
        </Button>
      </div>

      {openMode !== null && (
        <BulkDraftDrawResetModal
          open={openMode !== null}
          onClose={handleClose}
          onConfirm={handleConfirm}
          mode={openMode}
          preview={previewQuery.data}
          isLoadingPreview={previewQuery.isLoading}
          isRunning={isRunning}
          result={result}
        />
      )}
    </>
  );
}
